import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PageHeader from '../../../components/page/PageHeader';
import { useAuth } from '../../../context/AuthContext';
import { usePlan } from '../../../context/PlanContext';
import WikiFrame from '../shared/WikiFrame';
import WikiIcon, { WikiIconSquare } from '../shared/WikiIcon';
import { WikiEmpty, WikiError, WikiLoading } from '../shared/WikiStates';
import useOpenWikiAsk from '../shared/useOpenWikiAsk';
import useWikiRole from '../shared/useWikiRole';
import { useWikiCategories, useWikiHome } from '../shared/useWikiQueries';
import { wikiUi } from '../shared/wikiUi';
import { wikiPaths } from '../shared/wikiRoutes';
import { categoryColors, dateLabel, plural, versionLabel, wikiPathLabel } from '../shared/wikiFormat';
import './wikiHome.css';

/**
 * Wiki Home — route `/wiki` (spec A §1.5).
 *
 * Structure, copy, icons and accents = the prototype: header with New page /
 * Import / New category, the dark teal "Ask the Wiki" hero (fake input + chip
 * questions), then two columns — CATEGORIES card grid ⟷ "Needs your
 * acknowledgement", "Recently updated" and the AI Brain callout.
 * Frame, spacing and type = the CRM Dashboard + CRM core scale (REBUILD-PLAN §1):
 * PageHeader, `.wk-card`s on `--canvas`, 24px rhythm, 16/24px gaps.
 *
 * Data: `GET /wiki/home` (org name, counts, needs_ack, recent, unanswered this
 * week, suggestions) + `GET /wiki/categories` (the grid — the same cache the
 * nav and the create modals use, so a new category shows up everywhere).
 *
 * Plan (contracts/PLAN-CP.md, via PlanContext): "Import" hides when
 * `wiki_import` is off; a category-scoped chip hides when
 * `wiki_category_assistants` is off. While the plan is unknown everything
 * shows and the server refuses (403 `wiki_feature_disabled`). This view makes
 * no writes itself — the modals and Ask own those refusals.
 */

/** The prototype's hardcoded chips (`wkSuggest`) — used when the org has none. */
export const WIKI_HOME_SUGGESTIONS = Object.freeze([
    'Can I give 25% off on a 3-year enterprise deal?',
    'How many days a week can I work remotely?',
    'Can I share my MFA code with IT support?',
    "What's our response time for P1 escalations?",
]);

const BRAIN_CALLOUT_TAIL = 'The AI Brain lists the gaps by topic and tells each category owner.';

/** "GoTeam" → "GoTeam's", "Acme Systems" → "Acme Systems'", '' → "Your". */
export function orgPossessive(name) {
    const text = typeof name === 'string' ? name.trim() : '';
    if (!text) return 'Your';
    return /s$/i.test(text) ? `${text}'` : `${text}'s`;
}

/** Prototype `wkHomeSub`: "GoTeam's knowledge base · 5 categories · 13 pages". */
export function homeSubtitle({ orgName, categoryCount, pageCount }) {
    return [
        `${orgPossessive(orgName)} knowledge base`,
        plural(categoryCount, 'category', 'categories'),
        plural(pageCount, 'page'),
    ].join(' · ');
}

/** Bold sentence of the AI Brain callout; null when the count is unknown. */
export function unansweredSentence(count) {
    if (typeof count !== 'number' || !Number.isFinite(count)) return null;
    if (count <= 0) return 'No questions went unanswered this week.';
    return `${count === 1 ? '1 question' : `${count} questions`} went unanswered this week.`;
}

function pagePath(page) {
    return page?.path || wikiPathLabel(page?.category_name, page?.subcategory_name);
}

/** Right-hand label of a "Recently updated" row: "v4", or "Draft" for a never-published page (version 0). */
export function recentVersionLabel(page) {
    const version = Number(page?.version) || 0;
    if (page?.status === 'draft' || version <= 0) return 'Draft';
    return versionLabel(version);
}

function joinMeta(...parts) {
    return parts.filter((part) => part != null && part !== '').join(' · ');
}

function list(value) {
    return Array.isArray(value) ? value : [];
}

/**
 * Normalise `home.suggestions` into `[{question, categoryId}]`. The API sends
 * strings; an object `{question|text, category_id}` is a category-scoped chip,
 * dropped when the plan has no category assistants (`categoryAssistants`
 * false). Empty → the prototype's chips (never category-scoped).
 */
export function homeSuggestions(raw, { categoryAssistants = true } = {}) {
    const usable = list(raw).map((item) => {
        if (typeof item === 'string') return { question: item.trim(), categoryId: null };
        if (item && typeof item === 'object') {
            return {
                question: String(item.question || item.text || '').trim(),
                categoryId: item.category_id || item.categoryId || null,
            };
        }
        return null;
    }).filter((item) => item && item.question && (categoryAssistants || !item.categoryId));
    if (usable.length > 0) return usable;
    return WIKI_HOME_SUGGESTIONS.map((question) => ({ question, categoryId: null }));
}

// ── Header actions ─────────────────────────────────────────────────────────

function HomeActions({ showImport }) {
    return (
        <div className="wk-home-actions">
            <button
                type="button"
                className="wk-btn wk-btn--secondary"
                data-testid="wiki-home-new-page"
                onClick={() => wikiUi.openModal('page', { mode: 'write' })}
            >
                <WikiIcon name="file-text" size={15} />
                New page
            </button>
            {showImport && (
                <button
                    type="button"
                    className="wk-btn wk-btn--secondary"
                    data-testid="wiki-home-import"
                    onClick={() => wikiUi.openModal('page', { mode: 'import' })}
                >
                    <WikiIcon name="upload" size={15} />
                    Import
                </button>
            )}
            <button
                type="button"
                className="wk-btn wk-btn--primary wk-home-new-cat"
                data-testid="wiki-home-new-category"
                onClick={() => wikiUi.openModal('category', {})}
            >
                <WikiIcon name="plus" size={15} />
                New category
            </button>
        </div>
    );
}

// ── Ask the Wiki hero ──────────────────────────────────────────────────────

function AskHero({ orgName, suggestions, onAsk }) {
    return (
        <section className="wk-home-hero" aria-labelledby="wk-home-hero-title" data-testid="wiki-home-hero">
            <div className="wk-home-hero-head">
                <span className="wk-home-hero-badge" aria-hidden="true"><WikiIcon name="sparkles" size={16} /></span>
                <h2 id="wk-home-hero-title" className="wk-t-h wk-home-hero-title">Ask the Wiki</h2>
                <span className="wk-t-meta wk-home-hero-note">
                    Answers come only from pages you can read, with sources.
                </span>
            </div>
            <button
                type="button"
                className="wk-home-hero-input"
                data-testid="wiki-home-ask-input"
                onClick={() => onAsk()}
            >
                <span className="wk-home-hero-placeholder">
                    Ask anything about how {orgName || 'your team'} works...
                </span>
                <span className="wk-home-hero-send">
                    <WikiIcon name="send-horizontal" size={14} />
                    Ask
                </span>
            </button>
            {suggestions.length > 0 && (
                <div className="wk-home-chips" data-testid="wiki-home-chips">
                    {suggestions.map(({ question, categoryId }) => (
                        <button
                            key={`${categoryId || ''}|${question}`}
                            type="button"
                            className="wk-home-chip"
                            onClick={() => onAsk({ question, categoryId, autoSend: true })}
                        >
                            {question}
                        </button>
                    ))}
                </div>
            )}
        </section>
    );
}

// ── Categories ─────────────────────────────────────────────────────────────

function CategoryCard({ category, onOpen }) {
    const { color, tint } = categoryColors(category);
    const subs = category.subs || [];
    const meta = joinMeta(plural(category.page_count, 'page'), category.owner_name);
    return (
        // The card is one big click target (prototype); the name is the real
        // link for keyboard / screen-reader users, the chips are buttons.
        <div
            className="wk-card wk-card--interactive wk-home-card"
            data-testid={`wiki-home-cat-${category.id}`}
            onClick={() => onOpen(category.id)}
        >
            <div className="wk-home-card-head">
                <WikiIconSquare icon={category.icon} color={color} tint={tint} size={34} />
                <div className="wk-home-card-text">
                    <Link
                        to={wikiPaths.category(category.id)}
                        className="wk-t-name wk-home-card-name"
                        onClick={(event) => event.stopPropagation()}
                    >
                        {category.name}
                    </Link>
                    <div className="wk-t-meta wk-home-card-meta">{meta}</div>
                </div>
                <WikiIcon name="chevron-right" size={15} className="wk-home-card-chevron" />
            </div>
            {category.description && <p className="wk-t-dense wk-home-card-desc">{category.description}</p>}
            {subs.length > 0 && (
                <div className="wk-home-card-subs">
                    {subs.map((sub) => (
                        <button
                            key={sub.id}
                            type="button"
                            className="wk-home-sub-chip"
                            data-testid={`wiki-home-sub-${sub.id}`}
                            onClick={(event) => {
                                event.stopPropagation();
                                onOpen(sub.id);
                            }}
                        >
                            <WikiIcon name="folder" size={12} className="wk-home-sub-chip-icon" />
                            {sub.name}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

function CategoriesBlock({ categories, canEdit, onOpen }) {
    if (categories.isLoading) {
        return <div className="wk-card"><WikiLoading label="Loading categories…" /></div>;
    }
    if (categories.isError) {
        return (
            <div className="wk-card">
                <WikiError
                    error={categories.error}
                    title="Categories didn't load."
                    onRetry={() => categories.refetch()}
                />
            </div>
        );
    }
    if (categories.tree.length === 0) {
        return (
            <div className="wk-card">
                <WikiEmpty
                    icon="folder"
                    title="No categories yet"
                    detail={canEdit
                        ? 'Create a category to start organising pages.'
                        : 'Nothing has been published in the Wiki yet.'}
                    action={canEdit ? (
                        <button
                            type="button"
                            className="wk-btn wk-btn--primary wk-btn--sm"
                            onClick={() => wikiUi.openModal('category', {})}
                        >
                            <WikiIcon name="plus" size={15} />
                            New category
                        </button>
                    ) : null}
                />
            </div>
        );
    }
    return (
        <div className="wk-home-grid" data-testid="wiki-home-categories">
            {categories.tree.map((category) => (
                <CategoryCard key={category.id} category={category} onOpen={onOpen} />
            ))}
        </div>
    );
}

// ── Side column ────────────────────────────────────────────────────────────

function AckPanel({ pages, count }) {
    return (
        <section className="wk-card wk-home-ack" aria-labelledby="wk-home-ack-title" data-testid="wiki-home-ack">
            <h2 id="wk-home-ack-title" className="wk-t-h wk-home-ack-title">
                <WikiIcon name="badge-check" size={16} />
                <span>Needs your acknowledgement</span>
                <span className="wk-home-ack-count" data-testid="wiki-home-ack-count">{count}</span>
            </h2>
            <div className="wk-home-ack-list">
                {pages.map((page) => (
                    <Link
                        key={page.page_id}
                        to={wikiPaths.page(page.page_id)}
                        className="wk-home-row wk-home-ack-row"
                        data-testid={`wiki-home-ack-row-${page.page_id}`}
                    >
                        <WikiIcon name="file-text" size={15} className="wk-home-ack-icon" />
                        <span className="wk-home-row-text">
                            <span className="wk-t-row wk-home-row-name">{page.title}</span>
                            <span className="wk-t-meta wk-home-ack-meta">
                                {joinMeta(pagePath(page), versionLabel(page.version))}
                            </span>
                        </span>
                        <span className="wk-t-btn-sm wk-home-ack-cta">Read and confirm</span>
                    </Link>
                ))}
            </div>
        </section>
    );
}

function RecentCard({ pages }) {
    return (
        <section className="wk-card wk-home-recent" aria-labelledby="wk-home-recent-title" data-testid="wiki-home-recent">
            <h2 id="wk-home-recent-title" className="wk-t-h wk-home-recent-title">Recently updated</h2>
            {pages.length === 0 ? (
                <p className="wk-t-meta wk-home-recent-empty">No pages yet.</p>
            ) : pages.map((page) => (
                <Link
                    key={page.page_id}
                    to={wikiPaths.page(page.page_id)}
                    className="wk-home-row wk-home-recent-row"
                    data-testid={`wiki-home-recent-row-${page.page_id}`}
                >
                    <WikiIcon name="file-text" size={15} className="wk-home-recent-icon" />
                    <span className="wk-home-row-text">
                        <span className="wk-t-row wk-home-row-name">{page.title}</span>
                        <span className="wk-t-meta">{joinMeta(pagePath(page), dateLabel(page.updated_at))}</span>
                    </span>
                    <span className="wk-t-meta wk-home-recent-version">{recentVersionLabel(page)}</span>
                </Link>
            ))}
        </section>
    );
}

function BrainCallout({ unanswered }) {
    const sentence = unansweredSentence(unanswered);
    return (
        <Link to={wikiPaths.brain()} className="wk-home-brain" data-testid="wiki-home-brain">
            <WikiIcon name="brain" size={17} className="wk-home-brain-icon" />
            <span className="wk-t-dense wk-home-brain-text">
                {sentence && <b>{sentence}</b>}
                {sentence ? ' ' : ''}
                {BRAIN_CALLOUT_TAIL}
            </span>
        </Link>
    );
}

function SideColumn({ home, canBrainView }) {
    if (home.isLoading) {
        return <div className="wk-card"><WikiLoading label="Loading…" /></div>;
    }
    if (home.isError) {
        return (
            <div className="wk-card">
                <WikiError error={home.error} onRetry={() => home.refetch()} />
            </div>
        );
    }
    const data = home.data || {};
    const needsAck = list(data.needs_ack);
    const ackCount = typeof data.needs_ack_count === 'number' ? data.needs_ack_count : needsAck.length;
    return (
        <>
            {ackCount > 0 && needsAck.length > 0 && <AckPanel pages={needsAck} count={ackCount} />}
            <RecentCard pages={list(data.recent).slice(0, 5)} />
            {canBrainView && <BrainCallout unanswered={data.unanswered_this_week} />}
        </>
    );
}

// ── Page ───────────────────────────────────────────────────────────────────

export default function WikiHome() {
    const role = useWikiRole();
    const navigate = useNavigate();
    const openAsk = useOpenWikiAsk();
    const { user } = useAuth();
    const { checkFeature, planUnknown } = usePlan();
    const home = useWikiHome();
    const categories = useWikiCategories();

    const data = home.data || {};
    const orgName = data.org_name || user?.org_name || '';
    const categoryCount = categories.isSuccess ? categories.tree.length : data.category_count;
    const subtitle = home.isSuccess
        ? homeSubtitle({ orgName, categoryCount, pageCount: data.page_count })
        : undefined;
    // Plan unknown = show, and let the server refuse (PlanContext convention).
    const planAllows = (key) => planUnknown || checkFeature(key);
    const suggestions = home.isSuccess || home.isError
        ? homeSuggestions(data.suggestions, { categoryAssistants: planAllows('wiki_category_assistants') })
        : [];

    const openCategory = (id) => navigate(wikiPaths.category(id));

    return (
        <WikiFrame testId="wiki-home">
            <PageHeader
                title="Wiki"
                subtitle={subtitle}
                right={role.canEdit ? <HomeActions showImport={planAllows('wiki_import')} /> : null}
            />
            <AskHero orgName={orgName} suggestions={suggestions} onAsk={openAsk} />
            <div className="wk-home-body">
                <section className="wk-home-main" aria-labelledby="wk-home-categories-label">
                    <h2 id="wk-home-categories-label" className="wk-t-label wk-home-label">Categories</h2>
                    <CategoriesBlock categories={categories} canEdit={role.canEdit} onOpen={openCategory} />
                </section>
                <div className="wk-home-side" data-testid="wiki-home-side">
                    <SideColumn home={home} canBrainView={role.canBrainView} />
                </div>
            </div>
        </WikiFrame>
    );
}
